import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { catchError, Observable, throwError, timeout, retry, switchMap } from "rxjs";
import { DialogService } from "src/app/shared/services/dialog.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { SessionService } from "src/app/shared/services/session.service";
import en from 'src/assets/i18n/en';  

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
    translations = en;
    private _dialogService = inject(DialogService);
    private _loaderService = inject(LoaderService);
    private _sessionService = inject(SessionService);
    private readonly REQUEST_TIMEOUT = 30000; // 30 seconds
    private readonly MAX_RETRIES = 2;

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Skip retry for requests with X-Skip-Retry header
        const shouldRetry = !request.headers.has('X-Skip-Retry');
        
        let request$ = next.handle(request).pipe(
            timeout(this.REQUEST_TIMEOUT)
        );
        
        // Only add retry if not skipped
        if (shouldRetry) {
            request$ = request$.pipe(
                retry({
                    count: this.MAX_RETRIES,
                    delay: (error, retryCount) => {
                        // Only retry on network errors or 5xx errors
                        if (error instanceof HttpErrorResponse) {
                            const status = error.status;
                            if (status === 0 || (status >= 500 && status < 600)) {
                                return new Promise(resolve => setTimeout(resolve, retryCount * 1000));
                            }
                        }
                        throw error;
                    }
                })
            );
        }
        
        return request$.pipe(
            catchError((error: HttpErrorResponse) => {
                this._loaderService.forceHideLoader();
                
                if (error.status === 400 || error.status === 500) {
                    this._dialogService.showDialog(
                        this.translations.common.warning,
                        this.translations.common.somethingWentWrong
                    );
                } else if (error.status === 0) {
                    // Network error
                    this._dialogService.showDialog(
                        this.translations.common.warning,
                        'Network error. Please check your connection.'
                    );
                } else if (error.status === 401) {
                    const requestUrl = request.url.toLowerCase();
                    const isAuthenticationRequest = requestUrl.includes('/authenticate') ||
                        requestUrl.includes('/verify-otp') ||
                        requestUrl.includes('/generateotp') ||
                        requestUrl.includes('/refreshtoken');

                    const canRefresh = !isAuthenticationRequest &&
                        !request.headers.has('X-Skip-Session-Refresh') &&
                        !request.headers.has('X-Session-Retry');

                    if (canRefresh) {
                        return this._sessionService.refreshSession().pipe(
                            switchMap(refreshed => {
                                if (refreshed) {
                                    const token = localStorage.getItem('QNBNToken') || '';
                                    return next.handle(request.clone({
                                        setHeaders: {
                                            Authorization: `Bearer ${token}`,
                                            'X-Session-Retry': 'true'
                                        }
                                    }));
                                }

                                this._sessionService.endSession();
                                return throwError(() => error);
                            })
                        );
                    }

                    if (!isAuthenticationRequest && !request.headers.has('X-Skip-Session-Refresh')) {
                        this._sessionService.endSession();
                    }
                } else if (error.status === 403) {
                    // Forbidden
                    this._dialogService.showDialog(
                        this.translations.common.warning,
                        'You do not have permission to perform this action.'
                    );
                }
                
                return throwError(() => error);
            })
        );
    }
}
